import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { useNotifications } from '@hooks/useNotifications';
import { useCancelLockerCompartmentReservation } from '@services/useCancelLockerCompartmentReservation';
import { useGetMyStoresOrderDetail } from '@services/useGetMyStoresOrderDetail';

interface CancelLockerCompartmentReservationModalProps {
    orderUuid: string;
    storeUuid: string;
    reservationId: string | null;
    show: boolean;
    onClose: () => void;
}

export const CancelLockerCompartmentReservationModal: FC<CancelLockerCompartmentReservationModalProps> = ({
    orderUuid,
    storeUuid,
    reservationId,
    show,
    onClose,
}) => {
    const { showDefaultNotification } = useNotifications();
    const { request: requestMyStoresOrderDetail } = useGetMyStoresOrderDetail();
    const {
        request: requestCancelLockerCompartmentReservation,
        errorMessage,
        validationErrors,
        lastResponseTime,
    } = useCancelLockerCompartmentReservation();
    const { t } = useTranslation();

    const translations = {
        title: t('cancel-locker-compartment-reservation-modal-component.title'),
        description: t('cancel-locker-compartment-reservation-modal-component.description'),
        warning: t('cancel-locker-compartment-reservation-modal-component.warning'),
        confirm: t('cancel-locker-compartment-reservation-modal-component.confirm.button'),
    };

    const cancelBooking: () => void = () => {
        reservationId &&
            requestCancelLockerCompartmentReservation({
                order_uuid: orderUuid,
                store_uuid: storeUuid,
                reservation_id: reservationId,
            });
        onClose();
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                const firstError: string = Object.values(validationErrors).flat()[0] ?? '';
                showDefaultNotification({ type: 'error', message: firstError || '' });
            } else {
                // update page data
                requestMyStoresOrderDetail({ order_uuid: orderUuid, store_uuid: storeUuid });
                onClose();
            }
        }
    }, [lastResponseTime]);

    return (
        <PopupDelete
            question={translations.title}
            confirmation={translations.description}
            warning={translations.warning}
            confirmButtonText={translations.confirm}
            onClickConfirm={cancelBooking}
            show={show}
            onHide={() => onClose()}
        />
    );
};
